import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useAuth } from './AuthContext';
import { cartDB } from '../lib/supabase';
import { digitalProducts } from '../data/shopData';

const CartContext = createContext();

export const useCart = () => useContext(CartContext);

export const CartProvider = ({ children }) => {
    const { user } = useAuth();
    const [isCartOpen, setIsCartOpen] = useState(false);
    const [cartItems, setCartItems] = useState([]);
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const cartIdRef = useRef(null);

    // 1. Initial Load: Local Storage First
    useEffect(() => {
        const storedCart = localStorage.getItem('flipanythingusa_cart');
        if (storedCart) {
            try {
                setCartItems(JSON.parse(storedCart));
            } catch (e) {
                console.error("Failed to parse cart", e);
            }
        }
        setIsInitialLoad(false);
    }, []);

    // 2. Auth Change: Load or Merge Cart
    useEffect(() => {
        const handleAuthChange = async () => {
            if (user) {
                // Get or create cart for user
                const { data: cart, error: cartError } = await cartDB.getOrCreateCart(user.id);
                if (cartError) {
                    console.error("Error syncing cart", cartError);
                    return;
                }
                cartIdRef.current = cart.id;

                // Load database items
                const { data: dbItems, error: itemsError } = await cartDB.getCartItems(cart.id);
                if (itemsError) {
                    console.error("Error loading cart items", itemsError);
                    return;
                }

                // Merge Logic: Local Storage -> DB
                const localCart = JSON.parse(localStorage.getItem('flipanythingusa_cart') || '[]');
                if (localCart.length > 0) {
                    // Merge guest items into DB
                    for (const item of localCart) {
                        await cartDB.addItem(cart.id, {
                            productId: item.id,
                            packageName: item.packageName || item.category,
                            quantity: item.quantity,
                            extras: item.extras || []
                        });
                    }
                    // Clear local storage after successfully merging
                    localStorage.removeItem('flipanythingusa_cart');

                    // Reload final merged cart from DB
                    const { data: mergedItems } = await cartDB.getCartItems(cart.id);
                    setCartItems(transformDBItemsToCartItems(mergedItems || []));
                } else {
                    // No local items, just use DB items
                    setCartItems(transformDBItemsToCartItems(dbItems || []));
                }
            } else {
                // User logged out - handled by local storage effect
                cartIdRef.current = null;
            }
        };

        if (!isInitialLoad) {
            handleAuthChange();
        }
    }, [user, isInitialLoad]);

    // 3. DB Sync Filter: Update database when cart changes and user is logged in
    const syncToDB = async (items) => {
        if (!user || !cartIdRef.current) return;

        // Simple strategy: Clear and re-insert (Or smarter diffing)
        // For now, let's just sync local changes to DB for individual actions
    };

    // Helper: Map DB structure to UI structure
    const transformDBItemsToCartItems = (dbItems) => {
        return dbItems.map(item => {
            const product = digitalProducts.find(p => p.id === item.product_id);
            if (!product) return null;

            return {
                id: item.product_id,
                productId: item.product_id,
                dbItemId: item.id, // Store Supabase primary key for easy updates/deletes
                packageName: item.package_name,
                quantity: item.quantity,
                extras: item.extras,
                price: product.price || 0,
                title: product.title || 'Product',
                description: product.description || '', // Include description for Stripe checkout
                image: product.image
            };
        }).filter(Boolean);
    };

    // Save cart to local storage (always persist for page refresh)
    useEffect(() => {
        // Always save to localStorage for persistence on refresh
        // Even for logged-in users, localStorage provides immediate load before DB sync
        if (cartItems.length > 0 || !isInitialLoad) {
            localStorage.setItem('flipanythingusa_cart', JSON.stringify(cartItems));
        }
    }, [cartItems, isInitialLoad]);

    const addToCart = async (product) => {
        const newItems = [...cartItems];
        const existingIdx = newItems.findIndex(item => item.id === product.id);

        if (existingIdx > -1) {
            newItems[existingIdx].quantity += 1;
            if (user && cartIdRef.current) {
                // If we had internal item IDs from Supabase, we would update specific row
                // For simplicity, we'll implement a clean-sync strategy later
            }
        } else {
            newItems.push({ ...product, quantity: 1 });
            if (user && cartIdRef.current) {
                await cartDB.addItem(cartIdRef.current, {
                    productId: product.id,
                    packageName: product.packageName || product.category,
                    quantity: 1,
                    extras: product.extras || []
                });
            }
        }

        setCartItems(newItems);
        setIsCartOpen(true);
    };

    const removeFromCart = async (productId) => {
        if (user && cartIdRef.current) {
            const item = cartItems.find(i => i.id === productId);
            if (item?.dbItemId) {
                await cartDB.removeItem(item.dbItemId);
            }
        }
        setCartItems(prev => prev.filter(item => item.id !== productId));
    };

    const updateQuantity = async (productId, delta) => {
        let shouldRemove = false;
        const item = cartItems.find(i => i.id === productId);
        if (!item) return;

        const newQuantity = item.quantity + delta;
        if (newQuantity <= 0) {
            shouldRemove = true;
        }

        if (user && cartIdRef.current) {
            if (shouldRemove) {
                if (item.dbItemId) await cartDB.removeItem(item.dbItemId);
            } else {
                if (item.dbItemId) {
                    await cartDB.updateItem(item.dbItemId, { quantity: newQuantity });
                }
            }
        }

        if (shouldRemove) {
            setCartItems(prev => prev.filter(i => i.id !== productId));
        } else {
            setCartItems(prev => prev.map(i =>
                i.id === productId ? { ...i, quantity: newQuantity } : i
            ));
        }
    };

    const clearCart = async () => {
        if (user && cartIdRef.current) {
            await cartDB.clearCart(cartIdRef.current);
        }
        setCartItems([]);
    };
    const openCart = () => setIsCartOpen(true);
    const closeCart = () => setIsCartOpen(false);

    const cartCount = cartItems.reduce((acc, item) => acc + item.quantity, 0);
    const getTotal = () => cartItems.reduce((acc, item) => acc + (item.price * item.quantity), 0);
    const cartTotal = getTotal();

    return (
        <CartContext.Provider value={{
            isCartOpen,
            openCart,
            closeCart,
            cartItems,
            addToCart,
            removeFromCart,
            updateQuantity,
            clearCart,
            cartCount,
            cartTotal,
            getTotal
        }}>
            {children}
        </CartContext.Provider>
    );
};
